import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Mail } from "lucide-react";
import { RecrystaMark } from "@/components/RecrystaLogo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8">
        <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to login
        </Link>
        <div className="mt-4 flex items-center gap-2">
          <RecrystaMark size={36} />
          <div className="text-sm font-bold text-slate-900">Recrysta</div>
        </div>
        <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-slate-900">Reset your password</h1>
        <p className="mt-2 text-sm text-slate-500">Enter your email and we&apos;ll send you a link to set a new password.</p>

        {!sent ? (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-password-email"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
                placeholder="you@company.com"
              />
            </div>
            {err && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" data-testid="forgot-password-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-password-submit"
              className="w-full rounded-lg bg-slate-900 text-white py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        ) : (
          <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-4 text-sm text-emerald-900" data-testid="forgot-password-sent">
            <div className="flex items-center gap-2 font-semibold">
              <Mail className="h-4 w-4" />
              Check your inbox
            </div>
            <p className="mt-2 leading-relaxed">
              If an account exists for that email, we&apos;ve sent a reset link. It expires in 60 minutes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
